import { useMemo, useState } from 'react'

import useAppStore from '$appStore'

import SingleVenueSelector from './SingleVenueSelector'
import MultisiteVenueSelector from './MultisiteVenueSelector'

/**
 * The venue switcher for the React topbar.
 *
 * Lists the venues the user can access ordered by name, highlights the active
 * venue, and switches venue on selection.
 *
 * This has two modes (see `SingleVenueSelector` / `MultisiteVenueSelector`):
 *  - SINGLE-VENUE (default): pick one active venue. React writes the shared
 *    storage keys and hands off to AngularJS via `@@loaded/venueChange` where
 *    the app context is currently owned, when that changes it is announced
 *    back to React to make any changes it needs to
 *  - MULTISITE (when `multisiteEnabled`): a checkbox multi-select of venues with
 *    Select all / Unselect all, used while viewing a multisite report. On any
 *    change React persists the selection to `@@loaded/multisiteSelectedCompanies`
 *    and notifies AngularJS via `@@loaded/multisiteSelectionChange` so the
 *    open report rebuilds.
 *
 * This top-level component owns only the shared open/close state and the
 * mode decision; the trigger button, dropdown panel, and per-mode behaviour
 * live in their own files. Only renders when there is more than one venue,
 * matching the AngularJS directive's intent: a single-venue user has nothing to
 * switch to.
 * @returns the venue switcher, or `null` when there is nothing to switch between
 */
const VenueSwitcher = () => {
  const venues = useAppStore((state) => state.venues)
  const multisiteEnabled = useAppStore((state) => state.multisiteEnabled)
  const multisiteSelectedCompanies = useAppStore(
    (state) => state.multisiteSelectedCompanies
  )

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)

  // Venues are ordered alphabetically
  const sortedVenues = useMemo(
    () =>
      [...venues].sort((first, second) =>
        (first.shortName ?? '').localeCompare(second.shortName ?? '')
      ),
    [venues]
  )

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorElement(null)
  }

  // Nothing to switch between - don't render a dead control. (Checked before
  // the mode branch so neither mode renders for a single-venue user.)
  if (sortedVenues.length <= 1) {
    return null
  }

  if (multisiteEnabled) {
    return (
      <MultisiteVenueSelector
        venues={sortedVenues}
        selectedIds={multisiteSelectedCompanies}
        anchorElement={anchorElement}
        onOpen={handleOpen}
        onClose={handleClose}
      />
    )
  }

  return (
    <SingleVenueSelector
      venues={sortedVenues}
      anchorElement={anchorElement}
      onOpen={handleOpen}
      onClose={handleClose}
    />
  )
}

export default VenueSwitcher
